import { useContext } from 'react';
import { ListSubheader, List } from '@mui/material';
import { useLocation, matchPath } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { SidebarContext } from 'src/core/contexts/SidebarContext';
import { MenuItems, MenuItem } from 'src/core/types/appConfig';
import { drHerancaBranding } from 'src/drheranca/branding';
import SidebarMenuItem from './item';
import { styled } from '@mui/material/styles';

interface SidebarMenuProps {
  menuItems?: MenuItems[];
}


const MenuWrapper = styled(List, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(
  ({ theme, collapsed }) => `
    margin-bottom: ${theme.spacing(1)};
    padding: 0;

    & > .MuiList-root {
      padding: 0 ${collapsed ? theme.spacing(1) : theme.spacing(2)} ${theme.spacing(2)};
    }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: ${drHerancaBranding.fontWeight.bold};
      letter-spacing: 0.04em;
      font-size: ${theme.typography.pxToRem(11)};
      padding: ${theme.spacing(0.8, 1.75)};
      line-height: 1.4;
      color: ${drHerancaBranding.color.text.secondary};
      display: ${collapsed ? 'none' : 'block'};
    }
`
);

const SubMenuWrapper = styled(List, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(
  ({ theme, collapsed }) => `
    &.MuiList-root {
      padding: 0;

      .MuiList-root .MuiList-root .MuiListItem-root .MuiButton-root {
        font-weight: normal !important;
      }

      .MuiListItem-root {
        padding: 2px ${collapsed ? theme.spacing(1) : theme.spacing(1.5)};
    
        .MuiButton-root {
          display: flex;
          color: ${drHerancaBranding.color.text.menu};
          background-color: transparent;
          width: 100%;
          justify-content: ${collapsed ? 'center' : 'flex-start'};
          font-size: ${theme.typography.pxToRem(13)};
          padding-top: ${theme.spacing(0.8)};
          padding-bottom: ${theme.spacing(0.8)};
          position: relative;
          min-width: ${collapsed ? 'auto' : 'unset'};

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(4)};
            display: ${collapsed ? 'none' : 'block'};

            .MuiBadge-standard {
              background: ${theme.colors.primary.main};
              font-size: ${theme.typography.pxToRem(9)};
              font-weight: bold;
              text-transform: uppercase;
              color: ${theme.palette.primary.contrastText};
            }
          }
    
          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            font-size: ${theme.typography.pxToRem(19)};
            margin-right: ${collapsed ? 0 : theme.spacing(1)};
            color: ${drHerancaBranding.color.text.menu};
          }
          
          .MuiButton-endIcon {
            margin-left: auto;
            font-size: ${theme.typography.pxToRem(18)};
            display: ${collapsed ? 'none' : 'flex'};
          }

          & > span:not(.MuiButton-startIcon):not(.MuiButton-endIcon) {
            display: ${collapsed ? 'none' : 'inline'};
          }

          &:hover {
            background-color: ${drHerancaBranding.color.surface.icon};
            color: ${drHerancaBranding.color.text.primary};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
                color: ${drHerancaBranding.color.accent.main};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;
          line-height: 1;
        }

        .MuiCollapse-root {
          width: 100%;
          display: ${collapsed ? 'none' : 'block'};

          .MuiList-root {
            padding: ${theme.spacing(0.5, 0)};
            margin-left: ${theme.spacing(1.5)};
            border-left: 1px solid ${drHerancaBranding.color.border.default};
          }

          .MuiListItem-root {
            padding: 1px ${theme.spacing(0)};

            .MuiButton-root {
              font-size: ${theme.typography.pxToRem(12)};
              padding: ${theme.spacing(0.4, 1.5, 0.4, 2.25)};

              &.Mui-active,
              &:hover {
                background-color: ${drHerancaBranding.color.surface.icon};
              }
            }
          }
        }
      }
    }
`
);

const renderSidebarMenuItems = ({
  items,
  path,
  id,
  collapsed,
  depth,
}: {
  items: MenuItem[];
  path: string;
  id: string | undefined;
  collapsed?: boolean;
  depth: number;
}): JSX.Element => (
  <SubMenuWrapper collapsed={collapsed}>
    {items.reduce((ev, item) => reduceChildRoutes({ ev, item, path, id, collapsed, depth }), [])}
  </SubMenuWrapper>
);

const reduceChildRoutes = ({
  ev,
  path,
  item,
  id,
  collapsed,
  depth,
}: {
  ev: JSX.Element[];
  path: string;
  item: MenuItem;
  id: string | undefined;
  collapsed?: boolean;
  depth: number;
}): Array<JSX.Element> => {
  const key = item.name;
  // Resolve :id from current URL params without mutating config (so links always match current inventory)
  const link = item.link && id != null
    ? item.link.replace(/:id/g, id)
    : item.link;
  const currentPathname = path.split('?')[0];

  const exactMatch = link
    ? link.includes('?')
      ? link === path
      : !!matchPath({
          path: link,
          end: true
        }, currentPathname)
    : false;

  if (item.items) {
    const partialMatch = link
      ? link.includes('?')
        ? path.startsWith(link.split('&subtab=')[0])
        : !!matchPath({
            path: link,
            end: false
          }, currentPathname)
      : false;

    ev.push(
      <SidebarMenuItem
        key={key}
        active={partialMatch}
        open={partialMatch}
        name={item.name}
        icon={item.icon}
        link={link}
        badge={item.badge}
        collapsed={collapsed}
        depth={depth}
      >
        {renderSidebarMenuItems({
          path,
          items: item.items,
          id,
          collapsed,
          depth: depth + 1,
        })}
      </SidebarMenuItem>
    );
  } else {
    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        name={item.name}
        link={link}
        badge={item.badge}
        icon={item.icon}
        collapsed={collapsed}
        depth={depth}
      />
    );
  }

  return ev;
};

function SidebarMenu({ menuItems: propsMenuItems }: SidebarMenuProps) {
  const { sidebarCollapsed } = useContext(SidebarContext);
  const location = useLocation();
  let { id } = useParams();
  
  if (!propsMenuItems || propsMenuItems.length === 0) {
    return null;
  }
  
  const menuItemsCopy = Object.assign([], propsMenuItems);
  
  return (
    <>
      {menuItemsCopy.map((section) => (
        <MenuWrapper
          key={section.heading}
          collapsed={sidebarCollapsed}
          subheader={
            <ListSubheader component="div" disableSticky>{section.heading}</ListSubheader>
          }
        >
          {renderSidebarMenuItems({
            items: section.items,
            path: `${location.pathname}${location.search}`,
            id: id,
            collapsed: sidebarCollapsed,
            depth: 0,
          })}
        </MenuWrapper>
      ))}
    </>
  );
}

export default SidebarMenu;

